import { useState, useEffect, lazy, Suspense } from "react";
import { useParams, Link } from "react-router-dom";
import { apiUrl } from "./utils/api";
import { useLanguage } from "./context/LanguageContext";
import ThemeToggle from "./components/ThemeToggle";
import LanguageSwitcher from "./components/LanguageSwitcher";

const MarkdownRenderer = lazy(() => import("./components/MarkdownRenderer"));

const StaticPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const [page, setPage] = useState<{ title: string; content: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { t } = useLanguage();

  useEffect(() => {
    const fetchPage = async () => {
      try {
        setLoading(true);
        const response = await fetch(apiUrl(`/api/v1/common/pages/${slug}`));
        if (!response.ok) {
          throw new Error("Page not found");
        }
        const result = await response.json();
        setPage(result.data);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Error loading page");
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchPage();
    }
  }, [slug]);

  if (loading) return <div className="p-20 text-center text-foreground">Loading...</div>;
  if (error || !page) return <div className="p-20 text-center text-foreground">Page not found</div>;

  return (
    <div className="app-container min-h-screen flex flex-col">
      <header className="app-header floating-banner relative z-50">
        <Link to="/" className="text-2xl font-bold">BeeMerce</Link>
        <nav className="flex gap-6">
          <Link to="/">{t("nav.products")}</Link>
          <Link to="/about" className={slug === 'about' ? 'font-bold underline' : ''}>{t("nav.about")}</Link>
          <Link to="/contact" className={slug === 'contact' ? 'font-bold underline' : ''}>{t("nav.contact")}</Link>
        </nav>
        <div className="header-controls">
          <LanguageSwitcher />
          <ThemeToggle />
        </div>
      </header>

      <main className="flex-1 max-w-4xl mx-auto w-full p-8 pt-32">
        <h1 className="text-4xl font-bold mb-8 text-foreground">{page.title}</h1>
        <div className="prose dark:prose-invert max-w-none text-foreground">
          <Suspense fallback={<div>Loading content...</div>}>
            <MarkdownRenderer content={page.content} />
          </Suspense>
        </div>
      </main>

      <footer className="app-footer mt-auto p-8 text-center text-muted-foreground">
        <p>{t("footer.copyright")}</p>
      </footer>
    </div>
  );
};

export default StaticPage;
